import React from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube, Lock, MessageCircle } from "lucide-react";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "@/utils/languageRouter";

const Footer = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const currentLang = SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
  
  const localizedLink = (path) => {
    // Don't add language prefix for admin or collaborator routes
    if (path.startsWith("/admin") || path.startsWith("/collaborator")) {
      return path;
    }
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `/${currentLang}${cleanPath}`;
  };

  return (
    <footer className="bg-secondary text-secondary-foreground">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <span className="text-white font-serif text-xl">X</span>
              </div>
              <span className="font-serif text-2xl font-semibold">XploreSahara</span>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t("footer.tagline")}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-4">{t("footer.quickLinks")}</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to={localizedLink("/circuits/4x4")}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  data-testid="footer-circuits-4x4"
                >
                  {t("nav.circuits4x4")}
                </Link>
              </li>
              <li>
                <Link
                  to={localizedLink("/circuits/trekking")}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  data-testid="footer-trekking"
                >
                  {t("nav.trekking")}
                </Link>
              </li>
              <li>
                <Link
                  to={localizedLink("/map")}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  data-testid="footer-map"
                >
                  {t("nav.map")}
                </Link>
              </li>
              <li>
                <Link
                  to={localizedLink("/team")}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  data-testid="footer-team"
                >
                  {t("nav.team")}
                </Link>
              </li>
              <li>
                <Link
                  to={localizedLink("/experiences")}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-1"
                  data-testid="footer-experiences"
                >
                  <MessageCircle className="h-3 w-3" />
                  Retour d'expériences
                </Link>
              </li>
              <li>
                <Link
                  to={localizedLink("/cgv")}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  data-testid="footer-cgv"
                >
                  Conditions de vente
                </Link>
              </li>
            </ul>
          </div>

          {/* Departure Cities - SEO Links */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-4">
              {t("footer.departureFrom", "Départ de")}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to={localizedLink("/depart/marrakech")}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  data-testid="footer-depart-marrakech"
                >
                  Marrakech
                </Link>
              </li>
              <li>
                <Link
                  to={localizedLink("/depart/ouarzazate")}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  data-testid="footer-depart-ouarzazate"
                >
                  Ouarzazate
                </Link>
              </li>
              <li>
                <Link
                  to={localizedLink("/depart/agadir")}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  data-testid="footer-depart-agadir"
                >
                  Agadir
                </Link>
              </li>
              <li>
                <Link
                  to={localizedLink("/depart/zagora")}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  data-testid="footer-depart-zagora"
                >
                  Zagora
                </Link>
              </li>
              <li>
                <Link
                  to={localizedLink("/depart/mhamid")}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  data-testid="footer-depart-mhamid"
                >
                  M'hamid
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-4">{t("footer.contact")}</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <Mail className="h-4 w-4 text-primary" />
                <span>admin@saharatrek.org</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <Phone className="h-4 w-4 text-primary" />
                <span>+212 637-346823</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 text-primary mt-0.5" />
                <span>M'hamid El Ghizlane, Maroc</span>
              </li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-4">{t("footer.followUs")}</h4>
            <div className="flex gap-4">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors"
                data-testid="social-youtube"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} XploreSahara. {t("footer.rights")}
          </p>
          <div className="flex items-center gap-6">
            <p className="text-sm text-muted-foreground">
              Agence locale agréée N°RC-2847
            </p>
            <Link
              to={localizedLink("/cgv")}
              className="text-xs text-muted-foreground/80 hover:text-primary transition-colors"
              data-testid="footer-cgv-link"
            >
              Conditions Générales de Vente
            </Link>
            <Link
              to={localizedLink("/admin/login")}
              className="flex items-center gap-1.5 text-xs text-muted-foreground/60 hover:text-primary transition-colors"
              data-testid="footer-admin-link"
            >
              <Lock className="h-3 w-3" />
              <span>Espace Admin</span>
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
